import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Alert,
  Typography,
  Avatar,
} from "@mui/joy";
import { useAuthStore } from "../../store/authStore";
import { supabase } from "../../lib/supabase";

interface CompleteProfileFormProps {
  onSuccess: () => void;
}

export const CompleteProfileForm: React.FC<CompleteProfileFormProps> = ({
  onSuccess,
}) => {
  const { profile, updateProfile, loading, user } = useAuthStore();
  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    profile?.avatar_url || null
  );
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be less than 5MB");
      return;
    }

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!fullName.trim()) {
      setError("Full name is required");
      return;
    }

    if (!user) {
      setError("Not authenticated");
      return;
    }

    try {
      setUploading(true);
      let avatarPublicUrl = profile?.avatar_url || null;

      // Upload avatar if a new file was selected
      if (avatarFile) {
        const fileExt = avatarFile.name.split(".").pop();
        const filePath = `${user.id}/avatar.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(filePath, avatarFile, { upsert: true });

        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage.from("avatars").getPublicUrl(filePath);

        avatarPublicUrl = publicUrl;
      }

      const existingPreferences =
        (profile?.preferences as unknown as Record<string, unknown>) || {};
      await updateProfile({
        full_name: fullName.trim(),
        avatar_url: avatarPublicUrl,
        preferences: {
          ...existingPreferences,
          onboarding_completed: true,
        } as unknown as JSON,
      });

      onSuccess();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ textAlign: "left" }}
      data-testid="complete-profile-form"
    >
      {error && (
        <Alert color="danger" sx={{ mb: 2 }} data-testid="profile-form-error">
          {error}
        </Alert>
      )}

      {/* Avatar upload */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Avatar
          src={avatarPreview || undefined}
          sx={{
            width: 96,
            height: 96,
            mb: 1,
            cursor: "pointer",
            fontSize: "2rem",
            "&:hover": { opacity: 0.8 },
          }}
          onClick={() => fileInputRef.current?.click()}
          data-testid="avatar-preview"
        >
          {fullName ? fullName[0]?.toUpperCase() : "?"}
        </Avatar>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleAvatarSelect}
          style={{ display: "none" }}
          data-testid="avatar-file-input"
        />
        <Typography
          level="body-xs"
          sx={{
            color: "#6B6B6B",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          Click to upload photo
        </Typography>
      </Box>

      <FormControl sx={{ mb: 3 }}>
        <FormLabel>Full name</FormLabel>
        <Input
          required
          placeholder="Your full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          size="lg"
          data-testid="full-name-input"
        />
      </FormControl>

      <Button
        type="submit"
        fullWidth
        size="lg"
        loading={loading || uploading}
        disabled={!fullName.trim()}
        sx={{
          backgroundColor: "#FF6B4A",
          "&:hover": { backgroundColor: "#E55A3A" },
        }}
        data-testid="complete-profile-submit"
      >
        Continue
      </Button>
    </Box>
  );
};
